import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Spinner, Trash, ArrowSquareOut, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Demo Booked", "Closed"];
const tierCls = (s) => (s >= 75 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s >= 50 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-zinc-100 text-zinc-600 border-zinc-200");
const statusCls = { "New": "text-zinc-700", "Contacted": "text-sky-700", "Demo Booked": "text-emerald-700", "Closed": "text-zinc-400" };

export default function AdminProspects() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const load = () => api.get("/admin/prospects").then(({ data }) => setRows(data)).catch(() => toast.error("Failed to load prospects"));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { await api.delete(`/admin/prospects/${id}`); toast.success("Removed"); load(); };
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/prospects/${id}/status`, { status });
      setRows((r) => r.map((p) => (p.id === id ? data : p)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Could not update status"); }
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/prospects/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `orbit-prospects-${new Date().toISOString().slice(0, 10)}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const visible = rows ? [...rows].filter((p) => filter === "all" || p.status === filter).sort((a, b) => b.score - a.score) : [];

  return (
    <div className="space-y-5" data-testid="admin-prospects-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Prospect Scores</h1><p className="text-zinc-500 text-sm mt-1">Businesses evaluated with the Orbit Prospect Scoring Tool, highest first.</p></div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-11 w-40 border-zinc-300 font-semibold" data-testid="prospect-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={exportCsv} disabled={exporting || !rows?.length} variant="outline" className="h-11 gap-2 border-zinc-300 font-semibold" data-testid="prospect-export-csv">{exporting ? <Spinner size={18} className="animate-spin" /> : <DownloadSimple size={18} weight="bold" />}Export CSV</Button>
          <Link to="/prospect-score" target="_blank"><Button variant="outline" className="h-11 gap-2 border-zinc-300 font-semibold" data-testid="admin-open-prospect-tool"><ArrowSquareOut size={18} weight="bold" />Open scoring tool</Button></Link>
        </div>
      </div>
      {!rows ? <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div> : visible.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 border border-zinc-200 rounded-lg bg-white">{rows.length ? "No prospects with this status." : "No prospects scored yet."}</div>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
              <th className="px-4 py-2">Score</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Business</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Assets</th><th className="px-4 py-2">Movement</th><th className="px-4 py-2">Locations</th><th className="px-4 py-2">Tracking</th><th className="px-4 py-2">Date</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody className="text-zinc-700">
              {visible.map((p) => (
                <tr key={p.id} className={`border-b border-zinc-100 last:border-0 ${p.status === "Closed" ? "opacity-60" : ""}`} data-testid={`prospect-row-${p.id}`}>
                  <td className="px-4 py-2"><span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-bold ${tierCls(p.score)}`}>{p.score}<span className="font-medium text-xs">{p.tier}</span></span></td>
                  <td className="px-4 py-2">
                    <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                      <SelectTrigger className={`h-8 w-36 border-zinc-300 font-semibold ${statusCls[p.status] || ""}`} data-testid={`prospect-status-${p.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2 font-semibold text-zinc-900">{p.business_name}<div className="text-xs font-normal text-zinc-500">{p.industry}</div></td>
                  <td className="px-4 py-2">{p.contact_name}<div className="text-xs text-zinc-500">{p.email}{p.phone ? ` · ${p.phone}` : ""}</div></td>
                  <td className="px-4 py-2">{p.assets}</td><td className="px-4 py-2">{p.movement}</td><td className="px-4 py-2">{p.locations}</td>
                  <td className="px-4 py-2 max-w-[220px]">{p.tracking}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-zinc-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</td>
                  <td className="px-4 py-2"><Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="h-8 text-zinc-400 hover:text-red-600" data-testid={`prospect-delete-${p.id}`}><Trash size={16} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
